// ============================================
// INFRASTRUCTURE - Cliente Repository
// ============================================

import { axiosClient } from './axios-client';
import type { Cliente, PaginatedResponse, ClienteResumen } from '../../domain/entities';
import type { 
  CreateClienteDto, 
  UpdateClienteDto, 
  BuscarClienteDto, 
  ClienteFiadoResult, 
  ActualizarClienteFiadoDto,
  CrearClienteFiadoDto 
} from '../../domain/dtos';

export const clienteRepository = {
  /**
   * Listar clientes con paginación
   */
  async list(params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Cliente>> {
    const { data } = await axiosClient.get<PaginatedResponse<Cliente>>('/clientes', {
      params,
    });
    return data;
  },

  /**
   * Obtener cliente por ID
   */
  async getById(id: string): Promise<Cliente> {
    const { data } = await axiosClient.get<Cliente>(`/clientes/${id}`);
    return data;
  },

  /**
   * Buscar clientes (autocompletado)
   */
  async search(query: string): Promise<ClienteResumen[]> {
    const { data } = await axiosClient.get<ClienteResumen[]>('/clientes/search', {
      params: { q: query },
    });
    return data;
  },

  /**
   * Crear cliente
   */
  async create(dto: CreateClienteDto): Promise<Cliente> {
    const { data } = await axiosClient.post<Cliente>('/clientes', dto);
    return data;
  },

  /**
   * Actualizar cliente
   */
  async update(id: string, dto: UpdateClienteDto): Promise<Cliente> {
    const { data } = await axiosClient.patch<Cliente>(`/clientes/${id}`, dto);
    return data;
  },

  /**
   * Eliminar cliente (soft delete)
   */
  async delete(id: string): Promise<void> {
    await axiosClient.delete(`/clientes/${id}`);
  },

  /**
   * Buscar clientes para fiados por nombre, teléfono o RUT+DV
   * POST /clientes/find
   */
  async findClientesFiados(dto: BuscarClienteDto): Promise<ClienteFiadoResult[]> {
    const { data } = await axiosClient.post<ClienteFiadoResult[]>('/clientes/find', dto);
    return data;
  },

  /**
   * Actualizar cliente fiado
   * POST /clientes/update
   */
  async updateClienteFiado(dto: ActualizarClienteFiadoDto): Promise<void> {
    await axiosClient.post('/clientes/update', dto);
  },

  /**
   * Crear cliente fiado
   * POST /clientes
   */
  async createClienteFiado(dto: CrearClienteFiadoDto): Promise<ClienteFiadoResult> {
    const { data } = await axiosClient.post<ClienteFiadoResult>('/clientes', dto);
    return data;
  },

  /**
   * Guardar o actualizar alias de cliente en el almacén
   * PUT /fiados/clientes-almacen/{clienteAlmacenId}/nombre
   */
  async setNombreClienteAlmacen(clienteAlmacenId: number, nombre: string): Promise<void> {
    await axiosClient.put(`/fiados/clientes-almacen/${clienteAlmacenId}/nombre`, { nombre });
  },

  /**
   * Eliminar alias de cliente en el almacén
   * DELETE /fiados/clientes-almacen/{clienteAlmacenId}/nombre
   */
  async deleteNombreClienteAlmacen(clienteAlmacenId: number): Promise<void> {
    await axiosClient.delete(`/fiados/clientes-almacen/${clienteAlmacenId}/nombre`);
  },
};
